import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DeviceTypeParameterConfigWithAvailableListAndParameter } from '@vectronic/util';
import { BehaviorSubject, Subscription, combineLatest, distinctUntilChanged, interval } from 'rxjs';

type Config = DeviceTypeParameterConfigWithAvailableListAndParameter;

@Component({
  selector: 'vectronic-parameter-collars-config',
  templateUrl: './parameter-collars-config.component.html',
  styleUrls: ['./parameter-collars-config.component.scss'],
  imports: [
    MatFormField,
    MatLabel,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    ReactiveFormsModule,
    MatChipInput,
    MatTooltip,
    AsyncPipe,
    MatIconButton,
  ],
})
export class ParameterCollarsConfigComponent implements OnInit, OnDestroy {
  config = input<Config>();
  userValue = input<string>();
  disabled = input(false);
  readonly = input<boolean>();
  collarIds = input<number[]>();
  resetToFactoryTooltip = input<string>();

  hasClose = input(false);
  hideLabel = input<boolean>();
  showInfo = input(true);
  hasDelete = input(true);
  hasUndo = input(false);
  showReset = input<boolean>();
  disableSuggestion = input<boolean>(true);
  factoryValue = input<string>();

  factoryValueSubscription = toObservable(this.factoryValue)
    .pipe(takeUntilDestroyed())
    .subscribe((factoryValue) => {
      this.factoryCollars$.next(
        factoryValue
          ?.split(',')
          ?.filter((factoryCollar) => factoryCollar)
          ?.map((value) => parseInt(value)) || [],
      );
    });

  userValueSubscription = toObservable(this.userValue)
    .pipe(takeUntilDestroyed())
    .subscribe((userValue) => {
      this.selectedCollars$.next(
        userValue
          ?.split(',')
          ?.filter(Boolean)
          ?.map((value) => parseInt(value)) || [],
      );
    });

  disabledSubscription = toObservable(this.disabled)
    .pipe(takeUntilDestroyed())
    .subscribe((disabled) => {
      if (disabled) {
        this.collarCtrl.disable();
      } else {
        this.collarCtrl.enable();
      }
    });

  configClose = output();
  parameterChange = output<{ config: Config; value: string }>();
  parameterUndo = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  resetToFactory = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();

  selectedCollars$ = new BehaviorSubject<number[]>([]);
  factoryCollars$ = new BehaviorSubject<number[]>([]);

  collarCtrl = new FormControl('');
  separatorKeysCodes: number[] = [ENTER, COMMA];
  selectionSubscription: Subscription;
  showMore = true;

  @ViewChild('collarInput') collarInput: ElementRef<HTMLInputElement>;

  ngOnInit() {
    if (this.factoryValue()) {
      this.factoryCollars$.next(
        this.factoryValue()
          ?.split(',')
          ?.filter(Boolean)
          ?.map((value) => parseInt(value)) || [],
      );
    }
    this.selectedCollars$.next(
      this.userValue()
        ?.split(',')
        ?.filter(Boolean)
        ?.map((value) => parseInt(value)) || [],
    );

    this.selectionSubscription = combineLatest([this.selectedCollars$, interval(500)])
      .pipe(distinctUntilChanged(([val, time], [val2, time2]) => val === val2 || time === time2))
      .subscribe(([selectedCollars]) => {
        let selectedCollarIds = [];
        selectedCollars.forEach((selectedCollar) => {
          if (selectedCollar) selectedCollarIds.push(selectedCollar);
        });
        selectedCollarIds = selectedCollarIds.filter((item, pos, a) => a.indexOf(item) == pos);
      });
  }

  /**
   * Filters out factory IDs from the selected collars and returns a comma-separated string
   * @returns A comma-separated string of selected collars excluding factory IDs
   */
  private getValue(): string {
    if (!this.selectedCollars$.value || this.selectedCollars$.value.length === 0) {
      return undefined;
    }
    const filteredCollars = this.selectedCollars$.value;

    if (filteredCollars.length === 0) {
      return undefined;
    }
    return filteredCollars.join(',');
  }

  remove(collar: number) {
    this.selectedCollars$.next(this.selectedCollars$.value.filter((item) => item !== collar));
    this.parameterChange.emit({
      config: this.config(),
      value: this.getValue(),
    });
  }

  add() {
    if (!this.collarInput.nativeElement.value) return;

    if (this.collarInput.nativeElement.value.includes(',')) {
      this.addMultiple(this.collarInput.nativeElement.value);
      return;
    }

    if (
      parseInt(this.collarInput.nativeElement.value) <= 0 ||
      isNaN(parseInt(this.collarInput.nativeElement.value))
    )
      return;

    const newValue = parseInt(this.collarInput.nativeElement.value);
    if (this.selectedCollars$.value.includes(newValue)) return;
    if (this.factoryCollars$.value.includes(newValue)) return;

    this.selectedCollars$.next([...this.selectedCollars$.value, newValue]);
    this.collarCtrl.setValue(null);
    this.collarInput.nativeElement.value = '';
    this.parameterChange.emit({
      config: this.config(),
      value: this.getValue(),
    });
  }

  selected(event: MatAutocompleteSelectedEvent) {
    if (this.selectedCollars$.value.includes(event.option.value)) return;
    if (this.factoryCollars$.value.includes(event.option.value)) return;

    this.selectedCollars$.next([...this.selectedCollars$.value, event.option.value]);
    this.collarCtrl.setValue(null);
    this.collarInput.nativeElement.value = '';
    this.parameterChange.emit({
      config: this.config(),
      value: this.getValue(),
    });
  }
  ngOnDestroy() {
    this.selectionSubscription?.unsubscribe();
  }

  onClickRemove() {
    this.parameterChange.emit({ config: this.config(), value: undefined });
    this.collarCtrl.setValue('');
    this.selectedCollars$.next([]);
  }

  selectOption() {
    const enteredValue = this.collarCtrl.value;

    if (typeof enteredValue === 'string' && enteredValue.includes(',')) {
      this.addMultiple(enteredValue);
      return;
    }

    let didAdd = false;
    const newCollar = this.collarIds()?.find((item) => item === (enteredValue as any));
    if (
      newCollar &&
      !this.selectedCollars$.value.includes(newCollar) &&
      !this.factoryCollars$.value.includes(newCollar)
    ) {
      this.selectedCollars$.next([...(this.selectedCollars$.value as any), newCollar]);
      didAdd = true;
    }
    this.collarCtrl.setValue('');
    this.collarInput.nativeElement.value = '';
    if (didAdd) {
      this.parameterChange.emit({
        config: this.config(),
        value: this.getValue(),
      });
    }
  }

  get parameterTitle() {
    const { name, subcategory } = this.config().parameter;
    if (subcategory) return `${subcategory} / ${name}`;
    else return name;
  }

  onClickClose() {
    this.configClose.emit();
  }
  onClickUndo() {
    this.parameterUndo.emit(this.config());
    this.collarCtrl.setValue('');
    this.selectedCollars$.next([]);
  }

  onResetToFactory() {
    this.resetToFactory.emit(this.config());
  }

  onInputBlur() {
    if (this.collarInput?.nativeElement?.value) {
      if (this.collarInput.nativeElement.value.includes(',')) {
        this.addMultiple(this.collarInput.nativeElement.value);
      } else if (
        !isNaN(parseInt(this.collarInput.nativeElement.value)) &&
        parseInt(this.collarInput.nativeElement.value) > 0
      ) {
        this.add();
      }
    }
  }

  addMultiple(input: string) {
    if (!input) return;
    const values = Array.from(new Set(input.split(',').map((id) => id.trim())));
    const validIds: number[] = [];

    values.forEach((val) => {
      const trimmedVal = val.trim();
      if (trimmedVal && !isNaN(parseInt(trimmedVal)) && parseInt(trimmedVal) > 0) {
        validIds.push(parseInt(trimmedVal));
      }
    });

    if (validIds.length > 0) {
      const newValidIds = validIds.filter(
        (id) =>
          !this.selectedCollars$.value.includes(id) && !this.factoryCollars$.value.includes(id),
      );
      if (newValidIds.length === 0) return;

      this.selectedCollars$.next([...this.selectedCollars$.value, ...newValidIds]);

      this.collarCtrl.setValue(null);
      this.collarInput.nativeElement.value = '';

      this.parameterChange.emit({
        config: this.config(),
        value: this.getValue(),
      });
    }
  }
}
